import React, { useState } from 'react';
import Nav from '../../components/Nav';
import Footer from '../../components/Footer';
import { HelpCircle, Search, CreditCard, Award, BookOpen, UserCheck, ChevronDown, ChevronUp, Mail, ShieldCheck, Zap, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

const HelpSupport = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { title: 'Course Enrollment', desc: 'How to purchase & start learning', icon: <BookOpen className="text-emerald-600" size={24} />, bg: 'bg-emerald-50' },
    { title: 'Payments & Refunds', desc: 'UPI, Credit Cards & Invoice support', icon: <CreditCard className="text-blue-600" size={24} />, bg: 'bg-blue-50' },
    { title: 'Certificates', desc: 'Verified certificate generation & download', icon: <Award className="text-amber-600" size={24} />, bg: 'bg-amber-50' },
    { title: 'Account & Security', desc: 'Login, password reset & profile settings', icon: <UserCheck className="text-purple-600" size={24} />, bg: 'bg-purple-50' }
  ];

  const faqs = [
    {
      q: 'How do I enroll in a course?',
      a: 'Simply browse our available courses, click on any course to open the Course Overview page, click "Enroll Now", and complete payment via Cashfree, UPI, or Credit Card. Course access will be unlocked instantly in your Student Dashboard.'
    },
    {
      q: 'When will I receive my Certificate of Completion?',
      a: 'Once you complete 100% of the course lectures and pass the final exam with at least 60% score, your verified Certificate Of Completion will be automatically generated. You can view, download, and share it anytime from your Student Dashboard.'
    },
    {
      q: 'What payment methods are accepted?',
      a: 'We accept all major payment methods including Google Pay, PhonePe, Paytm, BHIM UPI, Visa/Mastercard Debit & Credit Cards, Net Banking, and Wallets via our secure Cashfree payment gateway.'
    },
    {
      q: 'Can I access the course on mobile devices?',
      a: 'Yes! E-Learning Hub is fully optimized for mobile web browsers, and you can also download our official mobile app to study on Android or iOS.'
    },
    {
      q: 'What if I encounter an issue with payment?',
      a: 'If your payment was debited but course access was not granted immediately, please contact our support team at support@learninghub.ind.in with your transaction reference. Our team resolves payment queries within 2 hours.'
    }
  ];

  const filteredFaqs = faqs.filter(f => f.q.toLowerCase().includes(searchTerm.toLowerCase()) || f.a.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-14 sm:pt-16">
      <Nav />
      <main className="flex-1 max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-5 w-full space-y-3.5 sm:space-y-5">
        
        {/* Compact Hero Search Header */}
        <div className="bg-gradient-to-r from-emerald-900 via-teal-900 to-gray-900 rounded-xl sm:rounded-2xl p-3.5 sm:py-4 sm:px-6 text-white text-center space-y-2 shadow-xs relative overflow-hidden">
          <div className="relative z-10 max-w-2xl mx-auto space-y-1">
            <span className="bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
              24/7 Student Support
            </span>
            <h1 className="text-sm sm:text-lg md:text-xl font-black leading-snug">How can we help you today?</h1>
            <p className="text-gray-300 text-[11px] sm:text-xs">Find answers to common questions regarding course enrollments, certificates, and payments.</p>

            <div className="max-w-md mx-auto relative pt-1">
              <Search className="absolute left-3.5 top-3.5 text-gray-400" size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search help topics (e.g. certificates, payments)..."
                className="w-full pl-10 pr-3 py-2 bg-white text-gray-900 rounded-xl shadow-2xs focus:outline-none text-xs font-medium"
              />
            </div>
          </div>
        </div>

        {/* Support Categories */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {categories.map((cat, idx) => (
            <div key={idx} className="bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-2xs space-y-1">
              <div className={`w-8 h-8 rounded-lg ${cat.bg} flex items-center justify-center`}>
                {cat.icon}
              </div>
              <h3 className="font-bold text-gray-900 text-xs sm:text-sm">{cat.title}</h3>
              <p className="text-[11px] text-gray-500 leading-normal">{cat.desc}</p>
            </div>
          ))}
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto space-y-2.5 w-full">
          <div className="text-center space-y-0.5 mb-3">
            <h2 className="text-xs sm:text-base font-bold text-gray-900">Frequently Asked Questions</h2>
            <p className="text-[11px] text-gray-500">Quick answers to common questions about E-Learning Hub</p>
          </div>

          {filteredFaqs.length === 0 ? (
            <div className="bg-white p-6 text-center rounded-xl border border-gray-100 text-gray-500 text-xs">
              No help topics matched your search term "{searchTerm}".
            </div>
          ) : (
            filteredFaqs.map((faq, index) => {
              const isOpen = activeFaq === index;
              return (
                <div key={index} className="bg-white rounded-xl border border-gray-100 shadow-2xs overflow-hidden transition-all">
                  <button
                    onClick={() => setActiveFaq(isOpen ? null : index)}
                    className="w-full text-left p-3 sm:p-3.5 flex items-center justify-between font-bold text-gray-900 hover:text-emerald-600 transition-colors text-xs sm:text-sm gap-2"
                  >
                    <span>{faq.q}</span>
                    {isOpen ? <ChevronUp className="text-emerald-600 shrink-0" size={16} /> : <ChevronDown className="text-gray-400 shrink-0" size={16} />}
                  </button>
                  {isOpen && (
                    <div className="px-3 sm:px-3.5 pb-3 pt-0 text-[11px] sm:text-xs text-gray-600 border-t border-gray-50 leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Contact CTA */}
        <div className="bg-emerald-50/80 border border-emerald-200/80 rounded-xl p-3.5 sm:p-5 text-center max-w-2xl mx-auto space-y-2">
          <MessageSquare className="w-6 h-6 text-emerald-600 mx-auto" />
          <h3 className="text-xs sm:text-base font-bold text-gray-900">Still need help?</h3>
          <p className="text-[11px] text-gray-600 max-w-md mx-auto">Our support team is available 24/7 to assist you with any questions or technical issues.</p>
          <div>
            <Link
              to="/contact-us"
              className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 rounded-lg font-bold text-xs hover:bg-emerald-700 transition-colors shadow-xs cursor-pointer"
            >
              <Mail size={14} /> Contact Support Team
            </Link>
          </div>
        </div>

      </main>
      <Footer />
    </div>
  );
};

export default HelpSupport;
